<template>
  <div>
    <v-app v-if="!digitalSignatureDialog" id="protocol" :dark="protocol.dark" :style="style" :class="companyShortName">
      <div id="loading" v-if="isLoading">
        <div id="error" v-if="errored">{{errorText}}</div>
        <div v-if="!errored">
          {{loadingText}}
          <p class="mt-3">
            <v-progress-circular :size="50" color="primary" indeterminate></v-progress-circular>
          </p>
        </div>
      </div>
      <div v-if="!isLoading">
        <v-navigation-drawer v-model="drawer" fixed app>
          <v-list dense>
            <template v-for="(step, index) in protocol.saved_values.steps">
              <v-list-tile :to="{name: step.vue_route_name, params: { page: step.uri}}" class="step" :key="index">
                <v-list-tile-action>
                  <v-icon>{{step.icon}}</v-icon>
                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title>
                    {{step.name}}
                    <v-icon v-if="step.finished" class="right">mdi-check-circle</v-icon>
                  </v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </template>
          </v-list>
        </v-navigation-drawer>
        <v-toolbar color="primary" :dark="protocol.dark" fixed app>
          <v-toolbar-side-icon @click.stop="drawer = !drawer"></v-toolbar-side-icon>
          <v-toolbar-title center>{{protocolTitle}}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon v-on:click.stop="customerServiceDialog = true">
            <v-icon>mdi-help-circle</v-icon>
          </v-btn>
        </v-toolbar>
        <v-content v-if="protocolOpenToEdit">
          <v-container fluid>
            <v-layout>
              <v-flex>
                <v-app>
                  <transition name="fade" mode="out-in">
                    <keep-alive>
                      <router-view
                        name="protocol"
                        @page-finished="pageFinished"
                        @page-not-finished="pageNotFinished"
                        @previous-page="previousPage"
                        @next-page="nextPage"
                        @error="error"
                        @save-state="saveState"
                        @showDigitalSignature="showDigitalSignature"
                        :protocol="protocol"
                        :step="currentStep"
                        :stepValues="currentStepValues"
                      ></router-view>
                    </keep-alive>
                  </transition>
                  <ErrorMessage v-model="errorMessage" @close-error="closeError"></ErrorMessage>
                  <ErrorMessage v-if="stateError" v-model="stateError" @close-error="closeStateErrorMessage"></ErrorMessage>
                  <v-snackbar v-model="snackbar" :multi-line="true">
                    {{snackbarText}}
                    <v-btn color="pink" flat @click="snackbar = false">Lukk</v-btn>
                  </v-snackbar>
                </v-app>
              </v-flex>
            </v-layout>
          </v-container>
        </v-content>
        <v-content v-else>
          <div v-if="!digitalSignatureDialog">Denne protokollen er allerede ferdig utfylt</div>
        </v-content>
        <v-footer color="grey" app v-if="!digitalSignatureDialog">
          <v-flex text-xs-center>
            <span
              class="white--text"
              style="padding-left:20px;"
            >{{companyName}} &copy; {{ new Date().getFullYear() }}</span>
            <v-offline online-class="online" offline-class="offline" @detected-condition="amIOnline">
              <template v-slot:[onlineSlot] :slot-name="onlineSlot">
                <v-icon
                  small
                  color="teal darken-2"
                  v-if="$store.state.online"
                >mdi-access-point-network</v-icon>
                <v-icon small color="red darken-2" v-else>mdi-access-point-network-off</v-icon>
              </template>
            </v-offline>
          </v-flex>
        </v-footer>
        <v-dialog v-model="customerServiceDialog" width="400" color="primary">
          <v-card>
            <v-card-title class="headline success" primary-title>Kundeservice</v-card-title>

            <v-card-text>
              <h4>Ved problemer med protokollen<br /> eller andre spørsmål:</h4>
              <h5 class="mt-4">Oppgi følgende referanse: {{$route.params.protocolHash}}</h5>
              <h5 class="mt-2">Epost: <v-btn href="mailto: support@digitalprotokoll.no" flat text small>support@digitalprotokoll.no</v-btn></h5>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" flat v-on:click.stop="customerServiceDialog = false">Lukk</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="isIe" width="400" color="primary">
          <v-card>
            <v-card-title class="headline warning" primary-title>Internet Explorer</v-card-title>
            <v-card-text>
              <h4>Vi har oppdaget at du bruker Internet Explorer</h4>
              På grunn av sikkerhetsmessige årsaker støttes ikke denne nettleseren. <br />
              Dette er en gammel nettleser som har mange kjente sikkerhetsfeil, og som ikke lenger støttes av Microsoft. <br />
              Vennligst forsøk å åpne denne siden på nytt med Chrome eller en annen moderne nettleser.
            </v-card-text>
          </v-card>
        </v-dialog>
      </div>
    </v-app>
    <v-app v-else id="protocol" :dark="protocol.dark" :style="style" :class="companyShortName">
      <DigitalSignature :protocol="protocol" :signRequest="signRequest" />
    </v-app>
  </div>
</template>
<script>
import Protocol from "../models/protocol";
import ErrorMessage from "./ErrorMessage.vue";
import DigitalSignature from "../components/DigitalSignature"
export default {
  metaInfo: {
    title: "Digital Protokoll",
    titleTemplate: "%s",
    htmlAttrs: {
      lang: "no",
      amp: true
    }
  },
  props: [],
  data() {
    return {
      customerServiceDialog: false,
      digitalSignatureDialog: false,
      signRequest: {
        name: ''
      },
      snackbar: false,
      snackbarText: "",
      drawer: null,
      isLoading: true,
      protocolTitle: "Protokoll",
      loadingText: "Vi henter frem info om protokoll",
      errorText: "Feil: Kunne dessverre ikke finne denne protokollen",
      errored: false,
      protocolOpenToEdit: true,
      onLine: true,
      onlineSlot: "online",
      offlineSlot: "offline",
      errorMessage: {
        active: false,
        title: "Feil",
        errorText: ""
      }
    };
  },
  components: { ErrorMessage, DigitalSignature },
  beforeMount() {
    if (this.$store.state.protocolHash) {
      if (this.$store.state.protocolHash != this.$route.params.protocolHash) {
        this.$store.commit("setProtocol", null);
      }
    }
    this.$store.commit("setProtocolHash", this.$route.params.protocolHash);
    this.axios
      .get(
        process.env.VUE_APP_PROTOCOL_API +
          "protocols/" +
          this.$store.state.protocolHash
      )
      .then(response => {
        if (!response.data) {
          this.errored = true;
          this.isLoading = false;
          this.errorText = "Feil: Kunne dessverre ikke finne denne protokollen";
        } else {
          let data = response.data;

          this.protocol.version = data.protocol_version;
          this.protocol.status = data.protocol_status_id;
          this.protocol.signRequest = data.sign_request ? data.sign_request.request : null;
          this.$vuetify.theme = this.getTheme();
          this.protocol.dark = this.getDark();
          this.protocol.logo = this.getLogo();

          if(this.protocol.status == 5) {
            this.protocolOpenToEdit = false;
            this.errored = true;
            this.errorText = "Denne protokollen er allerede ferdig signert";
          } else {
            if ( this.protocol.status == 4) {
              this.protocolOpenToEdit = false;
              this.errored = true;
              this.errorText = "Denne protokollen er fullført, og venter på signering";
              
              if(this.protocol.signRequest) {
                this.showDigitalSignature(this.protocol.signRequest);
              }
              return null
            }
            else {
              if (data.saved_values) {
                this.protocol.saved_values = JSON.parse(data.saved_values);
              } else {
                //console.log("Getting protocol_info");
                this.protocol.saved_values = {}
                this.protocol.saved_values.steps = this.protocol.version.steps;
                let defaultParticipants = {"landlords":[], "tenants":[], "buyers":[], "sellers":[], "images": []}
                let protocol_info = JSON.parse(data.protocol_info);
                for (let field in protocol_info) {
                  if(field === 'steps') {
                    for(let step in protocol_info.steps) {
                      if(this.protocol.saved_values.steps[step]) {
                        this.protocol.saved_values.steps[step].saved_values = Object.assign(this.protocol.saved_values.steps[step].saved_values, protocol_info.steps[step].saved_values);
                      }
                    }
                  }
                  else if(field == 'participants') {
                    this.protocol.saved_values.participants = Object.assign(defaultParticipants, protocol_info.participants)
                  }
                  else {
                    this.protocol.saved_values[field] = protocol_info[field]
                  }
                }
                if(!this.protocol.saved_values.participants) {
                  this.protocol.saved_values.participants = defaultParticipants
                }            
              }
            }
            this.protocol.hash = this.$route.params.protocolHash;
            this.isLoading = false;

            if(data.related_protocols) {
              if(data.related_protocols.length > 0) {
                this.protocol.related_protocol = JSON.parse(data.related_protocols[0].saved_values);
              }
            }
          }

          
          this.$store.commit("setProtocol", this.protocol);

          this.protocolTitle = this.protocol.version.protocol_definition.name;
          if (this.protocol.saved_values.address) {
            if (this.protocol.saved_values.address.street) {
              this.protocolTitle =
                this.protocol.version.protocol_definition.name +
                " - " +
                this.protocol.saved_values.address.street;
            }
          }
        }
      })
      .catch(error => {
        //this.errorText = error;
        console.log(error);
        this.errored = true;
      });
    this.amIOnline(true)
  },
  computed: {
    stateError: function() {
      if(this.$store.state.errorMessage != null) {
        return {
          active: true,
          title: "Feil",
          errorText: this.$store.state.errorMessage
        }
      }
      return null
    },
    isIe: {
      get() {
        let isIE = /*@cc_on!@*/false || !!document.documentMode
        return isIE
      }
    },
    protocol: {
      get() {
        if (!this.$store.state.protocol) {
          this.$store.commit("setProtocol", new Protocol());
        }
        return this.$store.state.protocol;
      },
      set(value) {
        this.$store.commit("setProtocol", value);
      }
    },
    currentStepIndex: function() {
      for (let key in this.protocol.version.steps) {
        if (
          key === this.$route.name
        ) {
          return key;
        }
        if(this.protocol.version.steps[key].pivot.config !== null && this.protocol.version.steps[key].pivot.config !== undefined) {
          let config = JSON.parse(this.protocol.version.steps[key].pivot.config)
          if(config.vue_uri !== null && config.vue_uri !== undefined) {
            if(config.vue_uri == this.$route.params.page) {
              return config.vue_storage_path
            }
          }
        }
      }
      return 'frontpage';
    },
    currentStep: function() {
      return this.protocol.version.steps[this.currentStepIndex]
    },
    currentStepValues: function() {
      return this.currentStep.saved_values
    },
    style: {
      get() {
        if (this.protocol.dark) {
          return "background: #000";
        } else {
          return "";
        }
      }
    },
    companyName: {
      get() {
        if (this.protocol.version) {
          return this.protocol.version.protocol_definition.department.company
            .name;
        } else {
          return "";
        }
      }
    },
    companyShortName: {
      get() {
        if (this.protocol.version) {
          return this.protocol.version.protocol_definition.department.company
            .short_name;
        } else {
          return "";
        }
      }
    }
  },
  methods: {
    showDigitalSignature: function(signRequest) {
      this.errored = false;
      this.isLoading = true;
      this.protocolOpenToEdit = false
      this.digitalSignatureDialog = true;
      this.signRequest = signRequest;
      const channel = this.$pusher.subscribe('idfy-'+signRequest.public_hash)
      channel.bind('idfy.update', (update) => {
        this.signRequest = update.request
      })
    },
    error: function(errorText) {
      this.errorMessage.errorText = errorText;
      this.errorMessage.active = true;
    },
    closeError: function() {
      this.errorMessage = {
        active: false,
        title: "Feil",
        errorText: ""
      }
    },
    closeStateErrorMessage: function() {
      this.$store.commit("setErrorMessage", null);
    },
    pageFinished: function() {
      this.protocol.saved_values.steps[this.currentStepIndex].finished = true;
      this.saveState();
    },
    pageNotFinished: function() {
      this.protocol.saved_values.steps[this.currentStepIndex].finished = false;
      this.saveState();
    },
    nextPage: function() {
      let keys = Object.keys(this.protocol.saved_values.steps);
      let nextIndex = keys.indexOf(this.currentStepIndex) +1;
      let nextItem = keys[nextIndex];
      this.pageFinished();
      if(nextItem != undefined && nextItem != null) {
        this.$router.push({
          name: this.protocol.saved_values.steps[nextItem].vue_route_name, params: { page: this.protocol.saved_values.steps[nextItem].uri}
        });
      }
    },
    previousPage: function() {
      let keys = Object.keys(this.protocol.saved_values.steps);
      let prevIndex = keys.indexOf(this.currentStepIndex)-1;
      let prevItem = keys[prevIndex];
      if(prevItem != undefined && prevItem != null) {
        this.$router.push({
          name: this.protocol.saved_values.steps[prevItem].vue_route_name, params: { page: this.protocol.saved_values.steps[prevItem].uri}
        });
      }
    },
    saveState() {
      let p = this.protocol;
      this.protocol = p;
    },
    amIOnline(e) {
      this.onLine = e;
      this.$store.commit("setOnline", e);
    },
    getTheme() {
      if (
        this.protocol.version.protocol_definition.department.options != null
      ) {
        if (
          this.protocol.version.protocol_definition.department.options.hasOwnProperty(
            "theme"
          )
        ) {
          return this.protocol.version.protocol_definition.department.options
            .theme;
        }
      }
      if (
        this.protocol.version.protocol_definition.department.company.options !=
        null
      ) {
        if (
          this.protocol.version.protocol_definition.department.company.options.hasOwnProperty(
            "theme"
          )
        ) {
          return this.protocol.version.protocol_definition.department.company
            .options.theme;
        }
      }
      return { primary: "#2C3E50", secondary: "#D6803D" }; // Default theme
    },
    getDark() {
      if (
        this.protocol.version.protocol_definition.department.options != null
      ) {
        if (
          this.protocol.version.protocol_definition.department.options.hasOwnProperty(
            "dark"
          )
        ) {
          return this.protocol.version.protocol_definition.department.options
            .dark;
        }
      }
      if (
        this.protocol.version.protocol_definition.department.company.options !=
        null
      ) {
        if (
          this.protocol.version.protocol_definition.department.company.options.hasOwnProperty(
            "dark"
          )
        ) {
          return this.protocol.version.protocol_definition.department.company
            .options.dark;
        }
      }
      return false;
    },
    getLogo() {
      if (
        this.protocol.version.protocol_definition.department.options != null
      ) {
        if (
          this.protocol.version.protocol_definition.department.options.hasOwnProperty(
            "logo"
          )
        ) {
          return this.protocol.version.protocol_definition.department.options
            .logo;
        }
      }
      if (
        this.protocol.version.protocol_definition.department.company.options !=
        null
      ) {
        if (
          this.protocol.version.protocol_definition.department.company.options.hasOwnProperty(
            "logo"
          )
        ) {
          return this.protocol.version.protocol_definition.department.company
            .options.logo;
        }
      }
      return "/images/logos/no_logo.png";
    }
  }
};
</script>

<style src="@/assets/styles/main.css">
</style>